import type { ConnectorType } from '@/types/connector';

export interface SearchConfigFieldDescriptor {
  name: string;
  label: string;
  type: 'text' | 'number' | 'select';
  placeholder: string;
  required: boolean;
  options?: string[];
  /** Optional short helper copy rendered via FieldHint below the field. */
  hint?: string;
}

const TABLE_NAME_HINT =
  'The table or view that holds your embeddings. Auto-discovered after a successful connection test for Postgres connectors.';
const VECTOR_COLUMN_HINT =
  'The column of type vector. This is what Gateco runs similarity search against.';
const ID_COLUMN_HINT =
  'The primary key column. Gateco uses it as the stable vector id when it gates each row.';
const CONTENT_COLUMN_HINT =
  'Optional but recommended. The text column returned as the chunk body. Required for keyword and hybrid search, and for grounded answers.';
const METRIC_HINT =
  'Must match how your index was built. cosine is the most common. A mismatch returns results in the wrong order, not an error.';
const EXPECTED_DIMENSION_HINT =
  'The dimension your vectors were embedded at (384 for all-MiniLM-L6-v2, 1536 for OpenAI text-embedding-3-small). This validates your corpus. It does NOT set the model Gateco uses to embed text queries. If you connected an existing corpus, set the query embedding model in the Embedding profile section below so text search matches your vectors.';

export const SEARCH_CONFIG_FIELDS: Record<ConnectorType, SearchConfigFieldDescriptor[]> = {
  pgvector: [
    { name: 'table_name', label: 'Table or view name', type: 'text', placeholder: 'embeddings', required: true, hint: TABLE_NAME_HINT },
    { name: 'vector_column', label: 'Vector column', type: 'text', placeholder: 'embedding', required: true, hint: VECTOR_COLUMN_HINT },
    { name: 'id_column', label: 'ID column', type: 'text', placeholder: 'id', required: true, hint: ID_COLUMN_HINT },
    { name: 'content_column', label: 'Content column', type: 'text', placeholder: 'content', required: false, hint: CONTENT_COLUMN_HINT },
    { name: 'metric', label: 'Distance metric', type: 'select', placeholder: 'cosine', required: false, options: ['cosine', 'l2', 'inner_product'], hint: METRIC_HINT },
    { name: 'expected_dimension', label: 'Expected dimension', type: 'number', placeholder: '1536', required: false, hint: EXPECTED_DIMENSION_HINT },
  ],
  supabase: [
    { name: 'table_name', label: 'Table or view name', type: 'text', placeholder: 'embeddings', required: true, hint: TABLE_NAME_HINT },
    { name: 'vector_column', label: 'Vector column', type: 'text', placeholder: 'embedding', required: true, hint: VECTOR_COLUMN_HINT },
    { name: 'id_column', label: 'ID column', type: 'text', placeholder: 'id', required: true, hint: ID_COLUMN_HINT },
    { name: 'content_column', label: 'Content column', type: 'text', placeholder: 'content', required: false, hint: CONTENT_COLUMN_HINT },
    { name: 'metric', label: 'Distance metric', type: 'select', placeholder: 'cosine', required: false, options: ['cosine', 'l2', 'inner_product'], hint: METRIC_HINT },
    { name: 'expected_dimension', label: 'Expected dimension', type: 'number', placeholder: '1536', required: false, hint: EXPECTED_DIMENSION_HINT },
  ],
  neon: [
    { name: 'table_name', label: 'Table or view name', type: 'text', placeholder: 'embeddings', required: true, hint: TABLE_NAME_HINT },
    { name: 'vector_column', label: 'Vector column', type: 'text', placeholder: 'embedding', required: true, hint: VECTOR_COLUMN_HINT },
    { name: 'id_column', label: 'ID column', type: 'text', placeholder: 'id', required: true, hint: ID_COLUMN_HINT },
    { name: 'content_column', label: 'Content column', type: 'text', placeholder: 'content', required: false, hint: CONTENT_COLUMN_HINT },
    { name: 'metric', label: 'Distance metric', type: 'select', placeholder: 'cosine', required: false, options: ['cosine', 'l2', 'inner_product'], hint: METRIC_HINT },
    { name: 'expected_dimension', label: 'Expected dimension', type: 'number', placeholder: '1536', required: false, hint: EXPECTED_DIMENSION_HINT },
  ],
  pinecone: [
    { name: 'index_name', label: 'Index name', type: 'text', placeholder: 'my-index', required: true },
    { name: 'namespace', label: 'Namespace', type: 'text', placeholder: 'default', required: false },
    { name: 'expected_dimension', label: 'Expected dimension', type: 'number', placeholder: '1536', required: false, hint: EXPECTED_DIMENSION_HINT },
  ],
  qdrant: [
    { name: 'collection_name', label: 'Collection name', type: 'text', placeholder: 'documents', required: true },
    { name: 'expected_dimension', label: 'Expected dimension', type: 'number', placeholder: '768', required: false, hint: EXPECTED_DIMENSION_HINT },
  ],
  weaviate: [
    { name: 'class_name', label: 'Class name', type: 'text', placeholder: 'Document', required: true },
    { name: 'properties', label: 'Properties (comma-separated)', type: 'text', placeholder: 'content', required: false },
    { name: 'expected_dimension', label: 'Expected dimension', type: 'number', placeholder: '1536', required: false, hint: EXPECTED_DIMENSION_HINT },
  ],
  milvus: [
    { name: 'collection_name', label: 'Collection name', type: 'text', placeholder: 'documents', required: true },
    { name: 'vector_field', label: 'Vector field', type: 'text', placeholder: 'embedding', required: false },
    { name: 'output_fields', label: 'Output fields (comma-separated)', type: 'text', placeholder: 'content', required: false },
    { name: 'expected_dimension', label: 'Expected dimension', type: 'number', placeholder: '384', required: false, hint: EXPECTED_DIMENSION_HINT },
  ],
  opensearch: [
    { name: 'index_name', label: 'Index name', type: 'text', placeholder: 'vectors', required: true },
    { name: 'vector_field', label: 'Vector field', type: 'text', placeholder: 'embedding', required: true },
    { name: 'expected_dimension', label: 'Expected dimension', type: 'number', placeholder: '1536', required: false, hint: EXPECTED_DIMENSION_HINT },
  ],
  chroma: [
    { name: 'collection_name', label: 'Collection name', type: 'text', placeholder: 'documents', required: true },
    { name: 'expected_dimension', label: 'Expected dimension', type: 'number', placeholder: '1536', required: false, hint: EXPECTED_DIMENSION_HINT },
  ],
  azure_ai_search: [
    { name: 'index_name', label: 'Index name', type: 'text', placeholder: 'my-index', required: true },
    { name: 'vector_field', label: 'Vector field', type: 'text', placeholder: 'contentVector', required: true },
    { name: 'content_field', label: 'Content field', type: 'text', placeholder: 'content', required: false },
    { name: 'key_field', label: 'Key field', type: 'text', placeholder: 'id', required: false },
    { name: 'expected_dimension', label: 'Expected dimension', type: 'number', placeholder: '1536', required: false, hint: EXPECTED_DIMENSION_HINT },
  ],
  vertex_ai_vector_search: [
    { name: 'expected_dimension', label: 'Expected dimension', type: 'number', placeholder: '768', required: false, hint: EXPECTED_DIMENSION_HINT },
  ],
  vertex_ai_search: [
    { name: 'serving_config_id', label: 'Serving Config ID (override)', type: 'text', placeholder: 'default_serving_config', required: false },
  ],
};

export type IngestionConfigFieldDescriptor = SearchConfigFieldDescriptor;

export const INGESTION_CONFIG_FIELDS: Record<ConnectorType, IngestionConfigFieldDescriptor[]> = {
  pgvector: [
    { name: 'target_table', label: 'Target table', type: 'text', placeholder: 'embeddings', required: true },
    { name: 'id_field', label: 'ID field', type: 'text', placeholder: 'id', required: false },
    { name: 'content_field', label: 'Content field', type: 'text', placeholder: 'content', required: false },
    { name: 'upsert_mode', label: 'Upsert mode', type: 'select', placeholder: 'insert', required: false, options: ['insert', 'upsert'] },
  ],
  supabase: [
    { name: 'target_table', label: 'Target table', type: 'text', placeholder: 'embeddings', required: true },
    { name: 'id_field', label: 'ID field', type: 'text', placeholder: 'id', required: false },
    { name: 'content_field', label: 'Content field', type: 'text', placeholder: 'content', required: false },
    { name: 'upsert_mode', label: 'Upsert mode', type: 'select', placeholder: 'insert', required: false, options: ['insert', 'upsert'] },
  ],
  neon: [
    { name: 'target_table', label: 'Target table', type: 'text', placeholder: 'embeddings', required: true },
    { name: 'id_field', label: 'ID field', type: 'text', placeholder: 'id', required: false },
    { name: 'content_field', label: 'Content field', type: 'text', placeholder: 'content', required: false },
    { name: 'upsert_mode', label: 'Upsert mode', type: 'select', placeholder: 'insert', required: false, options: ['insert', 'upsert'] },
  ],
  pinecone: [
    { name: 'target_index', label: 'Target index', type: 'text', placeholder: 'my-index', required: true },
    { name: 'namespace', label: 'Namespace', type: 'text', placeholder: 'default', required: false },
  ],
  qdrant: [
    { name: 'target_collection', label: 'Target collection', type: 'text', placeholder: 'documents', required: true },
  ],
  weaviate: [],
  milvus: [],
  opensearch: [],
  chroma: [],
  azure_ai_search: [],
  vertex_ai_vector_search: [],
  vertex_ai_search: [],
};

export interface FieldDescriptor {
  name: string;
  label: string;
  type: 'text' | 'number' | 'password' | 'textarea' | 'select';
  placeholder: string;
  required: boolean;
  secret: boolean;
  rows?: number;
  options?: string[];
  /** Optional short helper copy rendered via FieldHint below the field. */
  hint?: string;
}

const PGVECTOR_HOST_HINT =
  "These are your database's own connection details. Gateco connects as this user, so it needs read access to the table holding your vectors.";
const SCHEMA_HINT =
  'Defaults to public. Only change this if your vectors live in a non-default schema.';
const NEON_CONNECTION_STRING_HINT =
  'Paste the full Postgres URI from your Neon dashboard, including ?sslmode=require. ' +
  'Format: postgresql://user:password@ep-xxx.region.aws.neon.tech/dbname?sslmode=require';

export const CONNECTOR_FIELDS: Record<ConnectorType, FieldDescriptor[]> = {
  pgvector: [
    { name: 'host', label: 'Host', type: 'text', placeholder: 'db.example.com', required: true, secret: false, hint: PGVECTOR_HOST_HINT },
    { name: 'port', label: 'Port', type: 'number', placeholder: '5432', required: true, secret: false },
    { name: 'database', label: 'Database', type: 'text', placeholder: 'vectors', required: true, secret: false },
    { name: 'user', label: 'User', type: 'text', placeholder: 'postgres', required: false, secret: false },
    { name: 'password', label: 'Password', type: 'password', placeholder: '', required: false, secret: true },
    { name: 'schema', label: 'Schema', type: 'text', placeholder: 'public', required: false, secret: false, hint: SCHEMA_HINT },
  ],
  pinecone: [
    { name: 'api_key', label: 'API Key', type: 'password', placeholder: '', required: true, secret: true },
    { name: 'index_name', label: 'Index Name (optional filter)', type: 'text', placeholder: 'my-index', required: false, secret: false },
  ],
  opensearch: [
    { name: 'host', label: 'Endpoint URL', type: 'text', placeholder: 'https://...', required: true, secret: false },
    { name: 'api_key', label: 'API Key', type: 'password', placeholder: '', required: true, secret: true },
    { name: 'index_name', label: 'Index Name', type: 'text', placeholder: 'my-index', required: false, secret: false },
  ],
  supabase: [
    { name: 'host', label: 'Database Host', type: 'text', placeholder: 'db.abc123.supabase.co', required: true, secret: false },
    { name: 'port', label: 'Port', type: 'number', placeholder: '5432', required: false, secret: false },
    { name: 'database', label: 'Database', type: 'text', placeholder: 'postgres', required: false, secret: false },
    { name: 'user', label: 'User', type: 'text', placeholder: 'postgres', required: false, secret: false },
    { name: 'password', label: 'Password', type: 'password', placeholder: '', required: true, secret: true },
    { name: 'schema', label: 'Schema', type: 'text', placeholder: 'public', required: false, secret: false, hint: SCHEMA_HINT },
  ],
  neon: [
    { name: 'connection_string', label: 'Connection String', type: 'password', placeholder: 'postgresql://user:pass@ep-xyz.us-east-2.aws.neon.tech/dbname?sslmode=require', required: true, secret: true, hint: NEON_CONNECTION_STRING_HINT },
    { name: 'schema', label: 'Schema', type: 'text', placeholder: 'public', required: false, secret: false, hint: SCHEMA_HINT },
  ],
  weaviate: [
    { name: 'host', label: 'Cluster URL', type: 'text', placeholder: 'https://my-cluster.weaviate.network', required: true, secret: false },
    { name: 'api_key', label: 'API Key', type: 'password', placeholder: '', required: true, secret: true },
    { name: 'class_name', label: 'Class Name (optional filter)', type: 'text', placeholder: 'Document', required: false, secret: false },
  ],
  qdrant: [
    { name: 'host', label: 'Host URL', type: 'text', placeholder: 'https://xyz.cloud.qdrant.io:6333', required: true, secret: false },
    { name: 'api_key', label: 'API Key', type: 'password', placeholder: '', required: false, secret: true },
    { name: 'collection_name', label: 'Collection (optional filter)', type: 'text', placeholder: 'my-collection', required: false, secret: false },
  ],
  milvus: [
    { name: 'host', label: 'Host', type: 'text', placeholder: 'https://in01-xyz.aws.vectordb.zillizcloud.com', required: true, secret: false },
    { name: 'port', label: 'Port', type: 'number', placeholder: '19530', required: false, secret: false },
    { name: 'token', label: 'Token', type: 'password', placeholder: 'user:password or API token', required: false, secret: true },
    { name: 'collection_name', label: 'Collection (optional filter)', type: 'text', placeholder: 'my-collection', required: false, secret: false },
  ],
  chroma: [
    { name: 'host', label: 'Host URL', type: 'text', placeholder: 'http://localhost:8000', required: true, secret: false },
    { name: 'api_key', label: 'API Key', type: 'password', placeholder: '', required: false, secret: true },
    { name: 'tenant', label: 'Tenant', type: 'text', placeholder: 'default_tenant', required: false, secret: false },
    { name: 'database', label: 'Database', type: 'text', placeholder: 'default_database', required: false, secret: false },
  ],
  azure_ai_search: [
    { name: 'service_name', label: 'Service name', type: 'text', placeholder: 'my-search-service', required: true, secret: false },
    { name: 'api_key', label: 'API Key', type: 'password', placeholder: '', required: true, secret: true },
    { name: 'api_version', label: 'API version', type: 'text', placeholder: '2025-05-01-preview', required: false, secret: false },
  ],
  vertex_ai_vector_search: [
    { name: 'project_id', label: 'GCP Project ID', type: 'text', placeholder: 'my-gcp-project', required: true, secret: false },
    { name: 'region', label: 'Region', type: 'text', placeholder: 'us-central1', required: true, secret: false },
    { name: 'index_endpoint_id', label: 'Index Endpoint ID', type: 'text', placeholder: '1234567890123456789', required: true, secret: false },
    { name: 'deployed_index_id', label: 'Deployed Index ID', type: 'text', placeholder: 'my-deployed-index', required: true, secret: false },
    { name: 'public_endpoint_domain', label: 'Public Endpoint Domain', type: 'text', placeholder: '123456.us-central1-abc.vdb.vertexai.goog', required: true, secret: false },
    { name: 'distance_measure', label: 'Distance measure', type: 'select', placeholder: 'COSINE', required: false, secret: false, options: ['COSINE', 'DOT_PRODUCT_DISTANCE', 'SQUARED_L2_DISTANCE'] },
    { name: 'service_account_json', label: 'Service Account JSON', type: 'textarea', rows: 12, placeholder: '{\n  "type": "service_account",\n  "project_id": "...",\n  "private_key_id": "...",\n  ...\n}', required: true, secret: true },
  ],
  vertex_ai_search: [
    { name: 'project_id', label: 'GCP Project ID', type: 'text', placeholder: 'my-gcp-project', required: true, secret: false },
    { name: 'location', label: 'Location', type: 'text', placeholder: 'global', required: true, secret: false },
    { name: 'data_store_id', label: 'Data Store ID', type: 'text', placeholder: 'my-data-store_1234567890123', required: true, secret: false },
    { name: 'serving_config_id', label: 'Serving Config ID', type: 'text', placeholder: 'default_serving_config', required: false, secret: false },
    { name: 'service_account_json', label: 'Service Account JSON', type: 'textarea', rows: 12, placeholder: '{\n  "type": "service_account",\n  "project_id": "...",\n  "private_key_id": "...",\n  ...\n}', required: true, secret: true },
  ],
};
